<template>
  <page-wrapper error-key="accommodationObjects2" :transparent-bg="isGridView" sticky-second-row>
    <template #title>
      <div class="d-flex justify-space-between align-center w-100 h3">
        Объекты размещения

        <view-mode-toggle v-if="$vuetify.display.mdAndUp" v-model="viewMode" />
      </div>
    </template>

    <template #secondRow>
      <div class="d-flex align-center">
        <object-list-filters v-model="filter" :types="filterTypes" />
        <v-spacer />

        <e-btn
          v-if="isOperationVisible(accommodationObjects?.operations.canAdd)"
          prepend-icon-src="plus"
          :icon="$vuetify.display.smAndDown"
          :to="{ name: RouteNames.ObjectCreate }"
          variant="primary"
          class="object-list__add-btn"
          :disabled="isOperationDisabled(accommodationObjects?.operations.canAdd)"
        >
          <template v-if="!$vuetify.display.smAndDown">Добавить</template>
        </e-btn>
        <e-icon
          v-if="accommodationObjects?.operations.onApprovalLimitExceeded && !isLimitAlertVisible"
          name="alert"
          size="20"
          class="text-blue cursor-pointer ml-4"
          @click="setIsLimitAlertVisible(true)"
        />
      </div>
    </template>

    <error-boundary error-key="accommodationObjects2">
      <e-alert v-model="isLimitAlertVisible" variant="info" closable class="bg-white mb-4">
        <div class="paragraph-m_h font-600">Вы не можете добавить новый объект размещения</div>
        <div class="paragraph-s_h text-secondary mt-1">
          У вас два объекта размещения на &nbsp;рассмотрении. Вы сможете добавить новый объект размещения, когда один из
          &nbsp;них пройдёт проверку
        </div>
      </e-alert>

      <template v-if="isLoading || filteredObjects.length">
        <div v-if="isGridView" class="object-list__grid">
          <template v-if="isLoading">
            <skeleton-loader v-for="item in 10" height="200px" :key="item" />
          </template>
          <template v-else>
            <div
              v-for="item in filteredObjects"
              :key="item.cid"
              class="object-list__grid-item"
              @click="redirectToAccount(item.cid, item.contracts?.[0]?.contractNumber)"
            >
              <div class="object-list__grid-item-header">
                <status :status="item.commonInformation.status" class="mr-1" />
              </div>
              <v-img :src="'item.image'" aspect-ratio="1" cover class="mt-1 object-list__grid-item-image">
                <template #placeholder>
                  <div class="object-list__grid-item-placeholder">
                    <e-icon name="hotel" :size="48" />
                  </div>
                </template>
              </v-img>
              <div class="text-ordinary paragraph-s d-flex align-center mt-4">
                <e-icon name="mapPin" :size="16" class="mr-2" />
                {{ item.commonInformation.town }}
              </div>
              <div class="paragraph-m text-secondary mt-3">
                <b>{{ item.commonInformation.name[0].value }}</b>
              </div>
              <div class="paragraph-s text-ordinary mt-3 mb-5">
                {{ item.commonInformation.type }} / {{ item.commonInformation.category }}
              </div>

              <div v-if="item.contracts?.[0]" class="d-flex align-end justify-space-between mt-auto">
                <div>
                  <div class="text-ordinary paragraph-xs"><b>Аккаунт</b></div>
                  <div class="paragraph-s mt-1">
                    <b class="mr-2">
                      {{ item.contracts[0].contractNumber }}
                    </b>
                    <account-status :status="item.contracts[0].status" hide-text class="d-inline-block ml-2" />
                  </div>
                  <div class="paragraph-xs mt-1">
                    {{ item.contracts[0].legal.name[0].value }}
                  </div>
                  <div class="d-flex align-center paragraph-xs mt-1 text-secondary">
                    Оплата туроператором
                    <vertical-line class="ml-2 mr-2" />
                    {{ getCurrencyText(item.contracts[0].currencyISOCode) }}
                  </div>
                </div>
                <accounts-context-menu :accounts="item.contracts" :object-id="item.cid" />
              </div>
            </div>
          </template>
        </div>
        <e-data-table
          v-else
          class="mt-2"
          :headers="headers"
          :items="filteredObjects"
          :loading="isLoading"
          interactive
          content-wrap
          @on-row-click="onTableClick"
        >
          <template v-slot:[`header.image`]>
            <e-icon name="gallery" />
          </template>
          <template v-slot:[`item.image`]="{ value }">
            <div>
              <v-img :src="value" width="100%" aspect-ratio="1" cover class="object-list__table-item-image">
                <template #placeholder>
                  <div class="object-list__grid-item-placeholder">
                    <e-icon name="hotel" :size="24" />
                  </div>
                </template>
              </v-img>
            </div>
          </template>
          <template v-slot:[`item.type`]="{ item }">
            <div>{{ item.commonInformation.type }} / {{ item.commonInformation.category }}</div>
          </template>
          <template v-slot:[`item.status`]="{ item }">
            <div>
              <status :status="item.commonInformation.status" class="mr-1" />
            </div>
          </template>
          <template v-slot:[`item.contracts`]="{ value, item }">
            <div v-if="value && value.length" class="d-flex align-center justify-space-between">
              <div class="object-list__table-item-account mr-4">
                <div>
                  {{ value[0].contractNumber }}
                  <account-status :status="value[0].status" hide-text class="d-inline-block ml-2" />
                </div>
                <div class="d-flex align-center">
                  <span class="font-400 d-inline-block">{{ value[0].legal.name[0].value }}</span>
                  <vertical-line class="ml-2 mr-2" />
                  <span class="flex-shrink-0">{{ getCurrencyText(value[0].currencyISOCode) }}</span>
                </div>
              </div>
              <accounts-context-menu :accounts="value" :object-id="item.cid" />
            </div>
          </template>
        </e-data-table>
      </template>
      <page-content-placeholder
        v-else
        :img-src="PlaceholderImage"
        text="Объектов размещения не найдено
        Попробуйте изменить параметры фильтра"
        max-width="455px"
      />
    </error-boundary>
  </page-wrapper>
</template>

<script setup lang="ts">
import { computed, ref, toRef } from 'vue';
import { useRouter } from 'vue-router';
import { useDisplay } from 'vuetify';
import PlaceholderImage from '@/assets/img/pages/object/placeholder_image.svg';
import PageContentPlaceholder from '@/core/components/common/PageContentPlaceholder.vue';
import ViewModeToggle from '@/core/components/common/ViewModeToggle.vue';
import VerticalLine from '@/core/components/helpers/VerticalLine.vue';
import PageWrapper from '@/core/components/interface/PageWrapper.vue';
import SkeletonLoader from '@/core/components/interface/SkeletonLoader.vue';
import { EAlert, EBtn, EDataTable, EIcon } from '@/core/components/ui';
import { useFetch } from '@/core/composables/useFetch';
import useToggle from '@/core/composables/useToggle';
import { useUserPreference } from '@/core/composables/useUserPreferences';
import { RouteNames } from '@/core/plugins/router/routeNames';
import { ViewMode } from '@/core/types/components/helps';
import { getCurrencyText } from '@/core/utils/currency';
import { getSelectFilterItems } from '@/core/utils/filters';
import { isOperationDisabled, isOperationVisible } from '@/core/utils/permissions';
import { useComputedValue } from '@/core/utils/reactivity';
import { stringUrlSerializer, useQueryValue } from '@/core/utils/router/query-value';
import { containsSubstring } from '@/core/utils/string';
import ErrorBoundary from '@/parts/errors/components/ErrorBoundary.vue';
import AccountStatus from '@/parts/objects/components/AccountStatus.vue';
import AccountsContextMenu from '@/parts/objects/components/AccountsContextMenu.vue';
import ObjectListFilters from '@/parts/objects/components/ObjectListFilters.vue';
import Status from '@/parts/objects/components/Status.vue';
import { type ObjectListItemType, useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';
import { defaultFilters } from '@/parts/objects/utils/consts';

const $router = useRouter();
const { accommodationObjects, getAccounts } = useAccommodationObjectsStore();
const [isLimitAlertVisible, setIsLimitAlertVisible] = useToggle();

const viewMode = useUserPreference('objectPageViewMode', ViewMode.Table);

const filter = ref(defaultFilters());

useQueryValue(useComputedValue(filter, 'query'), 'query', stringUrlSerializer);
useQueryValue(useComputedValue(filter, 'type'), 'type', stringUrlSerializer);

const filteredObjects = computed(
  () =>
    accommodationObjects.value?.accommodationObjects.filter((object) => {
      if (
        filter.value.query &&
        !containsSubstring(object.commonInformation.name[0].value, filter.value.query) &&
        !containsSubstring(object.commonInformation.town, filter.value.query)
      ) {
        return false;
      }

      if (filter.value.type && object.commonInformation.type !== filter.value.type) {
        return false;
      }

      return true;
    }) || [],
);

const filterTypes = computed(() =>
  accommodationObjects.value
    ? getSelectFilterItems(accommodationObjects.value.accommodationObjects, 'type', 'commonInformation')
    : [],
);

const display = useDisplay();

const isGridView = toRef(() => viewMode.value === ViewMode.Grid || display.smAndDown.value);

const redirectToAccount = (id: string, acc_id: number | undefined) => {
  if (acc_id) {
    void $router.push({
      name: RouteNames.ObjectInfo,
      params: { id, acc_id: acc_id || 0 },
    });
  } else {
    void $router.push({
      name: RouteNames.ObjectSettings,
      params: { id, acc_id: acc_id || 0 },
    });
  }
};

const onTableClick = (_: Event, row: { item: { cid: string; contracts: { contractNumber: number }[] } }) => {
  redirectToAccount(row.item.cid, row.item.contracts[0]?.contractNumber);
};

const headers = [
  {
    key: 'image',
    sortable: false,
    width: display.lgAndDown.value ? 60 : 112,
  },
  {
    key: 'commonInformation.name[0].value',
    title: 'Объект размещения',
  },
  {
    key: 'type',
    title: 'Тип / Категория',
    sortRaw: (v1: ObjectListItemType, v2: ObjectListItemType) =>
      `${v2.commonInformation.type} / ${v2.commonInformation.category}` >
      `${v1.commonInformation.type} / ${v1.commonInformation.category}`
        ? -1
        : 1,
  },
  {
    key: 'commonInformation.town',
    title: 'Город',
  },
  {
    key: 'status',
    title: 'Статус',
    sortRaw: (v1: ObjectListItemType, v2: ObjectListItemType) =>
      v2.commonInformation.status > v1.commonInformation.status ? -1 : 1,
  },
  {
    key: 'contracts',
    title: 'Аккаунты',
    sortable: false,
    width: display.lgAndDown.value ? 240 : 360,
  },
];

const { isLoading } = useFetch(async () => {
  await getAccounts();

  if (accommodationObjects.value?.operations.onApprovalLimitExceeded) {
    setIsLimitAlertVisible(true);
  }
});
</script>

<style scoped lang="scss">
.object-list {
  &__grid {
    display: grid;

    grid-gap: $gap-4;
    grid-template-columns: repeat(4, 1fr);

    @include lg-and-down {
      grid-template-columns: repeat(3, 1fr);
    }

    @include md-and-down {
      grid-template-columns: repeat(2, 1fr);
    }

    @include xs {
      grid-template-columns: repeat(1, 1fr);
    }

    &-item {
      display: flex;
      flex-direction: column;

      padding: $gap-3 $gap-4 $gap-4;

      background-color: $main-bg-color;
      border-radius: $border-radius-l;

      cursor: pointer;

      @include hover {
        box-shadow: $border-box-shadow;
      }

      &-header {
        display: flex;
        justify-content: flex-end;
        align-items: center;
      }

      &-image {
        border-radius: 10px;
      }

      &-placeholder {
        display: flex;
        justify-content: center;
        align-items: center;

        height: 100%;

        color: $text-inactive-color;

        background-color: $body-bg-color;
      }
    }
  }

  &__table {
    &-item {
      &-image {
        border-radius: $border-radius-s;
      }

      &-account {
        max-width: 280px;
      }
    }
  }

  &__add-btn {
    @include sm-and-down {
      position: fixed;
      right: $gap-5;
      bottom: $gap-5;
      z-index: $z-index-3;
    }

    @include xs {
      right: $gap-4;
      bottom: $gap-4;
    }
  }
}
</style>
